import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { useGetPrintOnOptionsQuery, usePrintOnContractMutation } from '../../store/api/saleContractApi'

interface PrintOnDialogProps {
  open: boolean
  onClose: () => void
  contractId: number | null
  contractNo: string
  clientShort: string
  canEdit?: boolean
}

interface PrintOnForm {
  hopDong: string
  thanhToan: string
  chatLuong: string
  moTaChatLuong: string
}

const emptyPrintOnForm = (): PrintOnForm => ({
  hopDong: '',
  thanhToan: '',
  chatLuong: '',
  moTaChatLuong: '',
})

function buildPrintOnPreviewHtml(rawHtml: string): string {
  const htmlWithoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = htmlWithoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="print-on-preview-overrides">
@page{size:A4;margin:0;}
.print-action{display:none!important;}
.print-on-report .print-grid{gap:1.15mm!important;}
.print-on-report .print-row{grid-template-columns:30mm minmax(0,1fr) 23mm minmax(0,44mm)!important;min-height:3.9mm!important;}
.print-on-report .print-value{line-height:1.12!important;min-height:3.4mm!important;}
@media print{.report-page{width:210mm!important;max-width:none!important;min-height:297mm!important;padding:12mm 14mm!important;}}
</style>`
  if (withBase.includes('</head>')) {
    return withBase.replace('</head>', `${styleTag}</head>`)
  }
  return `${styleTag}${withBase}`
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

export default function PrintOnDialog({ open, onClose, contractId, contractNo, canEdit = true }: PrintOnDialogProps) {
  const [form, setForm] = useState<PrintOnForm>(emptyPrintOnForm)
  const [saving, setSaving] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [previewHtml, setPreviewHtml] = useState('')
  const [printOnContract] = usePrintOnContractMutation()

  const { data: options } = useGetPrintOnOptionsQuery(contractNo, {
    skip: !contractNo || !open,
  })

  useEffect(() => {
    if (options && open) {
      setForm({
        hopDong: options.contractNo ?? contractNo,
        thanhToan: options.paymentDescVn ?? '',
        chatLuong: options.quality ?? '',
        moTaChatLuong: options.qualityDescVn ?? '',
      })
    }
  }, [options, open, contractNo])

  const handleSaveAndPreview = async () => {
    if (!contractId) return
    setSaving(true)
    try {
      const result = await printOnContract({
        id: contractId,
        body: {
          thanhToan: form.thanhToan,
          moTaChatLuong: form.moTaChatLuong,
        },
      }).unwrap()
      setPreviewHtml(buildPrintOnPreviewHtml(result.html))
      setToastMessage(result?.message ?? 'Print preview generated.')
      onClose()
    } catch {
      // keep dialog open on error
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setForm(emptyPrintOnForm())
    setSaving(false)
    onClose()
  }

  return (
    <>
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Print Onshore Contract</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Contract" size="small" value={form.hopDong} InputProps={{ readOnly: true }} />
          <TextField
            label="Payment"
            size="small"
            multiline
            minRows={3}
            value={form.thanhToan}
            onChange={(e) => setForm((p) => ({ ...p, thanhToan: e.target.value }))}
            disabled={!canEdit}
          />
          <TextField label="Quality" size="small" value={form.chatLuong} InputProps={{ readOnly: true }} />
          <TextField
            label="Quality Description"
            size="small"
            multiline
            minRows={3}
            value={form.moTaChatLuong}
            onChange={(e) => setForm((p) => ({ ...p, moTaChatLuong: e.target.value }))}
            disabled={!canEdit}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} size="small">
          Cancel
        </Button>
        <Box sx={{ position: 'relative' }}>
          <Button variant="contained" size="small" onClick={handleSaveAndPreview} disabled={saving}>
            {canEdit ? 'Save & Preview' : 'Preview'}
          </Button>
          {saving && (
            <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-10px', ml: '-10px' }} />
          )}
        </Box>
      </DialogActions>
    </Dialog>
    <Dialog open={Boolean(previewHtml)} onClose={() => setPreviewHtml('')} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
        <Box component="span">Print Preview - Onshore Contract</Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => setPreviewHtml('')} size="small">
            Close
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={!canEdit}
            onClick={() => {
              const printTitle = `${sanitizePdfFileNamePart(contractNo)}_Sale_Contract_Onshore.pdf`
              void printAndUploadReport({
                iframeTitle: 'Print Onshore Contract Preview',
                reportName: 'rpContractSalesLocalSOn',
                printTitle,
                html: previewHtml,
                onUploadError: setToastMessage,
              })
            }}
          >
            Print
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
        <Box
          component="iframe"
          title="Print Onshore Contract Preview"
          srcDoc={previewHtml}
          sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
        />
      </DialogContent>
    </Dialog>
    <Snackbar
      open={Boolean(toastMessage)}
      message={toastMessage}
      autoHideDuration={2000}
      onClose={() => setToastMessage('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    />
    </>
  )
}
